import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Prompt } from 'react-router-dom';
import axios from 'axios';
import { debounce } from 'lodash';
import styled, { createGlobalStyle } from 'styled-components';
import { Checkbox as AntdCheckbox } from 'antd';
import {
  SearchOutlined,
  CheckCircleFilled,
  CloseOutlined,
  CheckOutlined,
  UserSwitchOutlined,
  UsergroupAddOutlined,
  LockOutlined,
  UserAddOutlined,
  MinusCircleOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import { Button, Modal, Tag, Input, Select, SelectOption } from 'components';
import LockboxStatusTag from 'components/LockboxStatusTag';
import { Loading } from '@app/react-common';
import errorReporter from '@app/errorReporter';
import { getSupraLockboxData } from 'api/SupraLockboxController';
import { AuthContextUser } from 'contexts';
import {
  isGlobalAdmin as checkIsGlobalAdmin,
  isAgent as checkIsAgent,
  isBranchAdmin as checkIsBranchAdmin,
  isTeamAdmin as checkIsTeamAdmin,
  isOwner as checkIsOwner,
  isManager as checkIsManager,
} from '@bb/users';
import type { IBranchAdminUser } from '@bb/users';
import SearchLabel from '../../Router/pages/QuickLockboxAssignmentPage/SearchLabel';

type PermissionMode = 'owner' | 'assignee' | 'shackle';
type AssignMode = 'assign' | 'unassign';
type ModalStep =
  | 'selectAction'
  | 'chooseType'
  | 'selectLockboxes'
  | 'review'
  | 'confirmation';

interface LockboxRow {
  id: string;
  serialNumber: string;
  boxType: string;
  owner: string | null;
  ownerId: string | null;
  ownerType: string | null;
  ownerOrgId: string | null;
  ownerBranchIndex: string | null;
  assignee: string | null;
  assigneeId: string | null;
  assigneeType: string | null;
  assigneeOrgId: string | null;
  shacklePermissions: number | null;
  granteeUserIds: string[];
  status: string;
}

interface ConfirmationResult {
  successful: number;
  unsuccessful: number;
  successfulIds?: string[];
  failedIds?: string[];
  failedIdsWithErrors?: { id: string; errorMessage: string }[];
  successfulLockboxes?: LockboxRow[];
  failedLockboxes?: (LockboxRow & { errorMessage?: string })[];
}

type OwnerAssigneeSearchResult = {
  id?: string;
  type?: string;
  orgId?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  officeName?: string;
  oid?: string;
  branchName?: string;
  address?: {
    line1?: string;
  };
};

type TargetSelection = {
  id: string;
  type: 'user' | 'office';
  label: string;
  orgId?: string;
};

export interface LockboxPermissionsManagerModalProps {
  visible: boolean;
  loggedInUser?: AuthContextUser;
  onConfirmAndProcess?: (payload: {
    mode: PermissionMode;
    assignMode: AssignMode;
    searchValue: string;
    ownerOrAssigneeType: string;
    target: TargetSelection | null;
    targetId: string;
    targetType: 'user' | 'office' | '';
    targetLabel: string;
    assigneeId?: string;
    assigneeType?: 'user' | 'office' | '';
    selectedIds: string[];
    selectedSerialNumbers: string[];
    lockboxAssigneeMap?: Record<string, string>;
    grantOfficeShacklePermissions?: boolean;
  }) => Promise<ConfirmationResult> | void;
  onCancel?: () => void;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'New', label: 'New' },
  { value: 'Used', label: 'Used' },
  { value: 'Assigned', label: 'Assigned' },
];

const AGENT_TYPE_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'office', label: 'Office' },
];

const WIZARD_STEPS = [
  { key: 'selectAction', label: 'Select Action' },
  { key: 'chooseType', label: 'Choose type' },
  { key: 'selectLockboxes', label: 'Select lockboxes' },
  { key: 'review', label: 'Review' },
] as const;

const PERMISSION_MODE_OPTIONS: {
  value: PermissionMode;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'owner',
    label: 'Change owner',
    description: 'Transfer ownership of selected lockboxes.',
    icon: <UserSwitchOutlined />,
  },
  {
    value: 'assignee',
    label: 'Change assignee',
    description: 'Assign or unassign offices and users.',
    icon: <UsergroupAddOutlined />,
  },
  {
    value: 'shackle',
    label: 'Change shackle permissions',
    description: 'Grant shackle access to user.',
    icon: <LockOutlined />,
  },
];

type UserRole =
  | 'staffAdmin'
  | 'globalAdmin'
  | 'branchAdmin'
  | 'teamAdmin'
  | 'agent';

const resolveUserRole = (user: AuthContextUser): UserRole | undefined => {
  const isStaff = user.org?.type === 'mlsStaff';
  const isAdminLevel =
    checkIsGlobalAdmin(user) || checkIsOwner(user) || checkIsManager(user);

  if (isStaff && isAdminLevel) return 'staffAdmin';
  if (isAdminLevel) return 'globalAdmin';
  if (checkIsBranchAdmin(user)) return 'branchAdmin';
  if (checkIsTeamAdmin(user)) return 'teamAdmin';
  if (checkIsAgent(user)) return 'agent';
  return undefined;
};

/**
 * Determines if a lockbox is owned by the MLS/Board/Association (not a user or office).
 */
const isLockboxOwnedByMLS = (lb: LockboxRow): boolean =>
  lb.ownerType === 'mls' || lb.ownerType === 'board';

/**
 * Determines if the logged-in user's org owns the lockbox.
 */
const isLockboxOwnedByMyOrg = (lb: LockboxRow, userOrgId: string): boolean =>
  !!lb.ownerOrgId && lb.ownerOrgId === userOrgId;

/**
 * Determines if the logged-in user's org is assigned to the lockbox.
 */
const isLockboxAssignedToMyOrg = (lb: LockboxRow, userOrgId: string): boolean =>
  !!lb.assigneeOrgId && lb.assigneeOrgId === userOrgId;

/**
 * Determines if the logged-in user personally owns the lockbox.
 */
const isLockboxOwnedByMe = (lb: LockboxRow, userId: string): boolean =>
  lb.ownerType === 'user' && lb.ownerId === userId;

/**
 * Determines whether the user can change the owner for a specific lockbox.
 * Rules:
 * - Staff Admin: always allowed
 * - Global/Branch Admin:
 *   - If their org OWNS the lockbox → allowed
 *   - If their org is the ASSIGNEE and owner is in the same org → allowed
 *   - If their org is the ASSIGNEE and owner is MLS or different org → NOT allowed
 * - Agent:
 *   - If the agent is the OWNER → allowed
 *   - If the agent is only the ASSIGNEE → NOT allowed
 */
const canChangeOwnerForLockbox = (
  lb: LockboxRow,
  role: UserRole,
  userId: string,
  userOrgId: string,
  userLocations?: string[],
): boolean => {
  if (role === 'staffAdmin') return true;

  if (role === 'globalAdmin' || role === 'branchAdmin') {
    // If org owns the lockbox directly
    if (isLockboxOwnedByMyOrg(lb, userOrgId)) {
      // Branch admin must also have the owner's location in their locations
      // ownerBranchIndex is only set for office/branch owner types, not for user owners
      if (
        role === 'branchAdmin' &&
        lb.ownerType !== 'user' &&
        lb.ownerBranchIndex &&
        userLocations &&
        !userLocations.includes(lb.ownerBranchIndex)
      ) {
        return false;
      }
      return true;
    }
    // If org is only assignee
    if (isLockboxAssignedToMyOrg(lb, userOrgId)) {
      // Can only change owner if the current owner is also in the same org
      if (isLockboxOwnedByMLS(lb)) return false;
      if (lb.ownerOrgId && lb.ownerOrgId !== userOrgId) return false;
      // Branch admin must also have the owner's location in their locations
      // ownerBranchIndex is only set for office/branch owner types, not for user owners
      if (
        role === 'branchAdmin' &&
        lb.ownerType !== 'user' &&
        lb.ownerBranchIndex &&
        userLocations &&
        !userLocations.includes(lb.ownerBranchIndex)
      ) {
        return false;
      }
      return true;
    }
    return true;
  }

  if (role === 'agent' || role === 'teamAdmin') {
    // Agent can only change owner if they ARE the owner
    return isLockboxOwnedByMe(lb, userId);
  }

  return false;
};

/**
 * Determines whether the user can change the assignee for a specific lockbox.
 * Rules:
 * - Staff Admin: always allowed
 * - Global/Branch Admin:
 *   - If their org OWNS the lockbox → allowed
 *   - If their org is the ASSIGNEE and owner is in the same org → allowed
 *   - If their org is the ASSIGNEE and owner is MLS or different org → NOT allowed
 * - Agent:
 *   - If the agent is the OWNER → allowed
 *   - If the agent is only the ASSIGNEE → NOT allowed
 */
const canChangeAssigneeForLockbox = (
  lb: LockboxRow,
  role: UserRole,
  userId: string,
  userOrgId: string,
  userLocations?: string[],
): boolean => {
  if (role === 'staffAdmin') return true;

  if (role === 'globalAdmin' || role === 'branchAdmin') {
    // If org owns the lockbox directly
    if (isLockboxOwnedByMyOrg(lb, userOrgId)) {
      // Branch admin must also have the owner's location in their locations
      // ownerBranchIndex is only set for office/branch owner types, not for user owners
      if (
        role === 'branchAdmin' &&
        lb.ownerType !== 'user' &&
        lb.ownerBranchIndex &&
        userLocations &&
        !userLocations.includes(lb.ownerBranchIndex)
      ) {
        return false;
      }
      return true;
    }
    // If org is only assignee
    if (isLockboxAssignedToMyOrg(lb, userOrgId)) {
      // Can only change assignee if the current owner is also in the same org
      if (isLockboxOwnedByMLS(lb)) return false;
      if (lb.ownerOrgId && lb.ownerOrgId !== userOrgId) return false;
      // Branch admin must also have the owner's location in their locations
      // ownerBranchIndex is only set for office/branch owner types, not for user owners
      if (
        role === 'branchAdmin' &&
        lb.ownerType !== 'user' &&
        lb.ownerBranchIndex &&
        userLocations &&
        !userLocations.includes(lb.ownerBranchIndex)
      ) {
        return false;
      }
      return true;
    }
    return true;
  }

  if (role === 'agent' || role === 'teamAdmin') {
    // Agent can only change assignee if they ARE the owner
    return isLockboxOwnedByMe(lb, userId);
  }

  return false;
};

/**
 * Determines whether the user can unassign the ASSIGNEE of a lockbox.
 * Rules:
 * - Staff Admin: always allowed
 * - Global/Branch Admin: allowed only if their org OWNS the lockbox
 * - Agent: allowed only if they are the OWNER of the lockbox
 */
const canUnassignAssigneeForLockbox = (
  lb: LockboxRow,
  role: UserRole,
  userId: string,
  userOrgId: string,
): boolean => {
  if (role === 'staffAdmin') return true;
  if (role === 'globalAdmin' || role === 'branchAdmin') {
    return isLockboxOwnedByMyOrg(lb, userOrgId);
  }
  if (role === 'agent' || role === 'teamAdmin') {
    return isLockboxOwnedByMe(lb, userId);
  }
  return false;
};

/**
 * Determines shackle permission granting scope for a specific lockbox.
 * Returns 'mls' (any agent in MLS/BOR + coop), 'office' (only within an office), or 'none'.
 *
 * Rules when user is the OWNER or Staff Admin:
 * - Can grant to any Agent within MLS/BOR + coop users → 'mls'
 *
 * Rules when user is ASSIGNEE (not owner):
 * - Global/Branch Admin:
 *   - MLS owner → 'mls'
 *   - Same brokerage Agent owner → 'mls'
 *   - Different brokerage Agent owner → 'mls'
 *   - Different brokerage Office owner → 'office' (assignee's office only)
 * - Agent:
 *   - MLS owner → 'mls'
 *   - Another Agent owner → 'mls'
 *   - Office owner → 'office' (their own office only, no coop users)
 */
type ShackleScope = 'mls' | 'office' | 'none';

const getShackleScopeForLockbox = (
  lb: LockboxRow,
  role: UserRole,
  userId: string,
  userOrgId: string,
): ShackleScope => {
  if (role === 'staffAdmin') return 'mls';

  // Check if user/org is the owner
  const isOwnerOfLockbox =
    role === 'agent' || role === 'teamAdmin'
      ? isLockboxOwnedByMe(lb, userId)
      : isLockboxOwnedByMyOrg(lb, userOrgId);

  if (isOwnerOfLockbox) return 'mls';

  // User is the assignee (not the owner)
  if (role === 'globalAdmin' || role === 'branchAdmin') {
    if (isLockboxOwnedByMLS(lb)) return 'mls';
    // Owner is another Agent (user) - check if same or different brokerage
    if (lb.ownerType === 'user') return 'mls';
    // Owner is an Office in a different brokerage
    if (lb.ownerType === 'office' && lb.ownerOrgId !== userOrgId)
      return 'office';
    return 'mls';
  }

  if (role === 'agent' || role === 'teamAdmin') {
    if (isLockboxOwnedByMLS(lb)) return 'mls';
    // Owner is another Agent
    if (lb.ownerType === 'user') return 'mls';
    // Owner is an Office → can only grant within their own office
    if (lb.ownerType === 'office') return 'office';
    return 'mls';
  }

  return 'none';
};

const buildFetchParams = (
  loggedInUser: AuthContextUser | undefined,
  page: number,
  statusFilter?: string,
  searchText?: string,
  permissionMode?: PermissionMode,
) => {
  if (!loggedInUser) return undefined;

  const role = resolveUserRole(loggedInUser);
  const lockboxStatuses = statusFilter
    ? [statusFilter]
    : ['New', 'Used', 'Assigned'];
  const commonParams = {
    lockboxStatuses,
    ...(searchText && { searchText }),
    page,
  };

  switch (role) {
    case 'staffAdmin':
      return {
        mlsStaffOrgId: loggedInUser.org.id,
        filterBySupraOwnerMapping: true,
        ...commonParams,
      };
    case 'globalAdmin':
      return {
        ownerOrgId: loggedInUser.org.id,
        ...(permissionMode === 'shackle' && {
          assigneeOrgId: loggedInUser.org.id,
        }),
        ...commonParams,
      };
    case 'branchAdmin':
      return {
        ownerBranchOrgId: loggedInUser.org.id,
        branchLocations:
          (loggedInUser as any as IBranchAdminUser).locations ||
          [(loggedInUser as any as IBranchAdminUser).location].filter(Boolean),
        ...(permissionMode === 'shackle' && {
          assigneeOrgId: loggedInUser.org.id,
        }),
        ...commonParams,
      };
    case 'teamAdmin':
    case 'agent':
      return {
        ownerId: loggedInUser.id ?? '',
        ownerType: 'user' as const,
        ...(permissionMode === 'shackle' && {
          assigneeId: loggedInUser.id ?? '',
          assigneeType: 'user' as const,
        }),
        ...commonParams,
      };
    default:
      return undefined;
  }
};

export const LockboxPermissionsManagerModal: React.FC<
  LockboxPermissionsManagerModalProps
> = ({ visible, loggedInUser, onConfirmAndProcess, onCancel }) => {
  // Wizard step
  const [step, setStep] = useState<ModalStep>('selectAction');

  // Selection step state
  const [permissionMode, setPermissionMode] = useState<PermissionMode>('owner');
  const [assignMode, setAssignMode] = useState<AssignMode>('assign');
  const [ownerOrAssigneeType, setOwnerOrAssigneeType] =
    useState<string>('user');
  const [grantOfficeShacklePermissions, setGrantOfficeShacklePermissions] =
    useState<boolean>(false);
  const [searchTarget, setSearchTarget] = useState<string>('');
  const [searchTargetLabel, setSearchTargetLabel] = useState<string>('');
  const [targetSelection, setTargetSelection] =
    useState<TargetSelection | null>(null);
  const [selectedTargetResult, setSelectedTargetResult] =
    useState<OwnerAssigneeSearchResult | null>(null);
  const [searchResults, setSearchResults] = useState<
    OwnerAssigneeSearchResult[]
  >([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [lockboxSearchInput, setLockboxSearchInput] = useState<string>('');
  const [lockboxSearch, setLockboxSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Fetched lockboxes for Change Owner mode
  const [fetchedLockboxes, setFetchedLockboxes] = useState<LockboxRow[]>([]);
  const [fetchedTotal, setFetchedTotal] = useState(0);
  const [lockboxesLoading, setLockboxesLoading] = useState(false);
  const [allFetchedRowsById, setAllFetchedRowsById] = useState<
    Record<string, LockboxRow>
  >({});
  // Incrementing this forces a lockbox refetch even if other deps haven't changed
  const [lockboxFetchTrigger, setLockboxFetchTrigger] = useState(0);

  // Review step state
  const [reviewIds, setReviewIds] = useState<string[]>([]);
  const REVIEW_PAGE_SIZE = 7;
  const [reviewVisibleCount, setReviewVisibleCount] =
    useState(REVIEW_PAGE_SIZE);
  const [skippedIds, setSkippedIds] = useState<Set<string>>(new Set());
  const [skipMessage, setSkipMessage] = useState<string>('');

  // Confirmation step state
  const [confirmationResult, setConfirmationResult] =
    useState<ConfirmationResult | null>(null);
  const [processing, setProcessing] = useState(false);
  const [confirmationTab, setConfirmationTab] = useState<
    'successful' | 'unsuccessful'
  >('successful');

  // Close-confirmation dialog state
  const [closeConfirmVisible, setCloseConfirmVisible] = useState(false);

  const extractEntityId = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }
    if (typeof value === 'object') {
      if ((value as any)._id) return String((value as any)._id);
      if ((value as any).id) return String((value as any).id);
    }
    return null;
  };

  const mapRawToRow = (lb: any): LockboxRow => ({
    id: String(lb._id),
    serialNumber: String(lb.serialNumber ?? ''),
    boxType: [lb.productCode, lb.serialNumber].filter(Boolean).join(' '),
    owner:
      lb.mlsOwnerName ||
      lb.branchOwnerName ||
      lb.orgOwnerName ||
      lb.officeOwnerName ||
      (lb.ownerType === 'user'
        ? [(lb.owner as any)?.firstName, (lb.owner as any)?.lastName]
            .filter(Boolean)
            .join(' ') || null
        : null),
    ownerId:
      lb.ownerType === 'mls' || lb.ownerType === 'board'
        ? null
        : extractEntityId(lb.owner),
    ownerType: lb.ownerType || null,
    ownerOrgId: lb.ownerOrgId || lb.ownerOrg || null,
    ownerBranchIndex: lb.ownerBranchIndex || null,
    assignee:
      lb.assigneeType === 'user'
        ? [(lb.assignee as any)?.firstName, (lb.assignee as any)?.lastName]
            .filter(Boolean)
            .join(' ') || null
        : lb.assigneeOfficeName || lb.officeAssigneeName || null,
    assigneeId:
      lb.assigneeType === 'user' || lb.assigneeType === 'office'
        ? extractEntityId(lb.assignee)
        : lb.assignee
          ? String(lb.assignee)
          : null,
    assigneeType: lb.assigneeType || null,
    assigneeOrgId: lb.assigneeOrgId || lb.assigneeOrg || null,
    shacklePermissions: (lb.granteeUserIds?.length ?? null) as number | null,
    granteeUserIds: (lb.granteeUserIds || []).map(String),
    status: lb.lockboxStatus || 'Unknown',
  });

  const fetchLockboxesForOwner = useCallback(
    async (
      page: number,
      status?: string,
      searchText?: string,
      mode?: PermissionMode,
    ) => {
      setLockboxesLoading(true);
      try {
        const params = buildFetchParams(
          loggedInUser,
          page,
          status,
          searchText,
          mode,
        );
        const { lockboxes: raw, lockboxCount } =
          await getSupraLockboxData(params);
        const rows = raw.map(mapRawToRow);
        setFetchedLockboxes(prev => (page > 1 ? [...prev, ...rows] : rows));
        setFetchedTotal(lockboxCount);
        // Merge into accumulated map so rows from all visited pages are available
        setAllFetchedRowsById(prev => {
          const next = { ...prev };
          rows.forEach(row => {
            next[row.id] = row;
          });
          return next;
        });
      } catch (err) {
        setFetchedLockboxes([]);
        setFetchedTotal(0);
        errorReporter.error(err);
      } finally {
        setLockboxesLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedInUser],
  );

  // Always use API-fetched lockboxes for all permission modes
  const activeSource = fetchedLockboxes;

  const displayedLockboxes = useMemo(() => activeSource, [activeSource]);

  const reviewLockboxes = useMemo(
    () =>
      reviewIds
        .map(
          id => allFetchedRowsById[id] ?? activeSource.find(lb => lb.id === id),
        )
        .filter((lb): lb is LockboxRow => !!lb),
    [reviewIds, allFetchedRowsById, activeSource],
  );

  const allDisplayedSelected =
    displayedLockboxes.length > 0 &&
    displayedLockboxes.every(lb => selectedIds.has(lb.id));

  const someSelected = selectedIds.size > 0;

  const selectionState = useMemo(() => {
    const rows = Array.from(selectedIds)
      .map(
        id => allFetchedRowsById[id] ?? activeSource.find(lb => lb.id === id),
      )
      .filter((lockbox): lockbox is LockboxRow => Boolean(lockbox));

    const emptyState = {
      validIds: rows.map(({ id }) => id),
      skippedIds: new Set<string>(),
      warningMessage: '',
      skipMessage: '',
    };

    if (!targetSelection || assignMode === 'unassign') {
      return emptyState;
    }

    // Shackle mode: detect lockboxes where the target user already has shackle permission
    if (permissionMode === 'shackle') {
      const alreadyGrantedIds = new Set<string>();
      const alreadyGrantedSerials: string[] = [];
      const shackleValidIds: string[] = [];

      rows.forEach(lockbox => {
        if (
          targetSelection.type === 'user' &&
          lockbox.granteeUserIds.includes(targetSelection.id)
        ) {
          alreadyGrantedIds.add(lockbox.id);
          alreadyGrantedSerials.push(lockbox.serialNumber);
        } else {
          shackleValidIds.push(lockbox.id);
        }
      });

      const shackleWarningMessage = alreadyGrantedSerials.length
        ? `Serial number${alreadyGrantedSerials.length === 1 ? '' : 's'} (${alreadyGrantedSerials.join(', ')}) already ${alreadyGrantedSerials.length === 1 ? 'has' : 'have'} shackle permissions for ${searchTargetLabel}.`
        : '';

      const shackleSkipMessage = alreadyGrantedSerials.length
        ? `Serial number${alreadyGrantedSerials.length === 1 ? '' : 's'} (${alreadyGrantedSerials.join(', ')}) already ${alreadyGrantedSerials.length === 1 ? 'has' : 'have'} shackle permissions for ${searchTargetLabel} and will be skipped.`
        : '';

      return {
        validIds: shackleValidIds,
        skippedIds: alreadyGrantedIds,
        warningMessage: shackleWarningMessage,
        skipMessage: shackleSkipMessage,
      };
    }

    const noOpIds = new Set<string>();
    const conflictIds = new Set<string>();
    const noOpSerials: string[] = [];
    const conflictSerials: string[] = [];
    const validIds: string[] = [];

    // For office targets, the search API returns a composite ID: "orgId-0-officeId-0-0"
    // We need to extract the officeId (second part) for matching against lockbox.ownerId/assigneeId
    const isOfficeTarget = targetSelection.type === 'office';
    let officeId = '';
    if (isOfficeTarget && targetSelection.id.includes('-0-')) {
      const parts = targetSelection.id.split('-0-');
      officeId = parts[1] || ''; // Extract officeId from composite ID
    }

    const matchesOwner = (lockbox: LockboxRow) => {
      if (!isOfficeTarget) {
        return (
          lockbox.ownerId === targetSelection.id &&
          lockbox.ownerType === targetSelection.type
        );
      }
      // For office: match officeId against ownerId, or full ID against ownerOrgId
      return (
        lockbox.ownerId === officeId ||
        lockbox.ownerOrgId === targetSelection.id
      );
    };

    const matchesAssignee = (lockbox: LockboxRow) => {
      if (!isOfficeTarget) {
        return (
          lockbox.assigneeId === targetSelection.id &&
          lockbox.assigneeType === targetSelection.type
        );
      }
      // For office: match officeId against assigneeId, or full ID against assigneeOrgId
      return (
        lockbox.assigneeId === officeId ||
        lockbox.assigneeOrgId === targetSelection.id
      );
    };

    rows.forEach(lockbox => {
      const isNoOp =
        permissionMode === 'owner'
          ? matchesOwner(lockbox)
          : matchesAssignee(lockbox);

      if (isNoOp) {
        noOpIds.add(lockbox.id);
        noOpSerials.push(lockbox.serialNumber);
        return;
      }

      const isConflict =
        permissionMode === 'owner'
          ? matchesAssignee(lockbox)
          : matchesOwner(lockbox);

      if (isConflict) {
        conflictIds.add(lockbox.id);
        conflictSerials.push(lockbox.serialNumber);
        return;
      }

      validIds.push(lockbox.id);
    });

    const action = permissionMode === 'owner' ? 'owned by' : 'assigned to';
    const oppositeRole = permissionMode === 'owner' ? 'assignee' : 'owner';
    const warningMessage = [
      noOpSerials.length &&
        `Serial number${noOpSerials.length === 1 ? '' : 's'} (${noOpSerials.join(', ')}) ${noOpSerials.length === 1 ? 'is' : 'are'} already ${action} ${searchTargetLabel}.`,
      conflictSerials.length &&
        `Serial number${conflictSerials.length === 1 ? '' : 's'} (${conflictSerials.join(', ')}) cannot be processed because ${searchTargetLabel} is already the ${oppositeRole}.`,
    ]
      .filter(Boolean)
      .join(' ');

    const skipSummary = [
      noOpSerials.length &&
        `Serial number${noOpSerials.length === 1 ? '' : 's'} (${noOpSerials.join(', ')}) ${noOpSerials.length === 1 ? 'is' : 'are'} already ${action} ${searchTargetLabel} and will be skipped.`,
      conflictSerials.length &&
        `Serial number${conflictSerials.length === 1 ? '' : 's'} (${conflictSerials.join(', ')}) will be skipped because ${searchTargetLabel} is already the ${oppositeRole}.`,
    ]
      .filter(Boolean)
      .join(' ');

    return {
      validIds,
      skippedIds: new Set([...noOpIds, ...conflictIds]),
      warningMessage,
      skipMessage: skipSummary,
    };
  }, [
    selectedIds,
    allFetchedRowsById,
    activeSource,
    targetSelection,
    permissionMode,
    assignMode,
    searchTargetLabel,
  ]);

  const isAssigneeMode = permissionMode === 'assignee';

  const currentFieldLabel = isAssigneeMode
    ? 'Current Assignee'
    : permissionMode === 'owner'
      ? 'Current Owner'
      : 'Current Permission';

  const newFieldLabel = isAssigneeMode
    ? 'New Assignee'
    : permissionMode === 'owner'
      ? 'New Owner'
      : 'New Permission';

  const hasErrors = confirmationResult && confirmationResult.unsuccessful > 0;
  const allSuccess =
    confirmationResult && confirmationResult.unsuccessful === 0;

  // Unsaved changes: modal is open, not yet on the final confirmation screen,
  // and the user has made at least one selection or is mid-review/processing.
  const hasUnsavedChanges =
    visible &&
    step !== 'confirmation' &&
    (someSelected ||
      step === 'chooseType' ||
      step === 'selectLockboxes' ||
      step === 'review' ||
      processing ||
      Boolean(targetSelection) ||
      Boolean(searchTarget));

  const getSearchResultDisplayName = (item: OwnerAssigneeSearchResult) => {
    if (item.type === 'office') {
      const officeName = item.officeName || item.name || 'Office';
      return item.oid ? `${officeName} #${item.oid}` : officeName;
    }

    const userName =
      item.name ||
      [item.firstName, item.lastName].filter(Boolean).join(' ').trim();

    return userName || item.id || 'Unknown';
  };

  const getSelectedTargetInitials = (
    item: OwnerAssigneeSearchResult | null,
  ): string => {
    if (!item) return '';

    const first = (item.firstName || '').trim();
    const last = (item.lastName || '').trim();
    if (first || last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }

    const officeSource = (item.officeName || item.name || '').trim();
    if (officeSource) {
      const parts = officeSource.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return officeSource.slice(0, 2).toUpperCase();
    }

    return 'NA';
  };

  const getSelectedTargetTitle = (item: OwnerAssigneeSearchResult | null) => {
    if (!item) return '';

    if (item.type === 'office') {
      return item.officeName || item.name || 'Office';
    }

    const fullName = [item.firstName, item.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return fullName || item.name || 'Unknown user';
  };

  const getSelectedTargetSubtitle = (
    item: OwnerAssigneeSearchResult | null,
  ) => {
    if (!item) return '';

    if (item.type === 'office') {
      const officeAddress = item.address?.line1 || '';
      return [officeAddress, item.oid ? `OID: ${item.oid}` : '']
        .filter(Boolean)
        .join(' · ');
    }

    return item.branchName || '';
  };

  const handleTargetSearch = useCallback(
    async (term: string) => {
      if (!term) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);
      try {
        const queryString =
          `searchTerm=${encodeURIComponent(term)}` +
          '&includeUserMlsId=1&includeOfficeId=1&includeOfficeName=1&size=10&page=0' +
          `&type=${ownerOrAssigneeType}` +
          '&fromPermissionsManager=1';
        const {
          data: { searchResult = [] },
        } = await axios.get(`/lockbox/owner_and_assignee?${queryString}`);
        setSearchResults(searchResult);
      } catch (err) {
        setSearchResults([]);
        errorReporter.error(err);
      } finally {
        setSearchLoading(false);
      }
    },
    [ownerOrAssigneeType],
  );

  const debouncedTargetSearch = useMemo(
    () => debounce(handleTargetSearch, 1000),
    [handleTargetSearch],
  );

  useEffect(
    () => () => {
      debouncedTargetSearch.cancel();
    },
    [debouncedTargetSearch],
  );

  useEffect(() => {
    if (!visible) return;
    fetchLockboxesForOwner(
      currentPage,
      statusFilter,
      lockboxSearch || undefined,
      permissionMode,
    );
  }, [
    visible,
    permissionMode,
    ownerOrAssigneeType,
    currentPage,
    fetchLockboxesForOwner,
    lockboxFetchTrigger,
    statusFilter,
    lockboxSearch,
  ]);

  // Reset to page 1 when status filter or search text changes to avoid stale pagination
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, lockboxSearch]);

  const debouncedSetLockboxSearch = useMemo(
    () =>
      debounce((val: string) => {
        if (val === '' || val.length >= 3) {
          setCurrentPage(1);
          setLockboxSearch(val);
        }
      }, 1000),
    [],
  );

  useEffect(
    () => () => {
      debouncedSetLockboxSearch.cancel();
    },
    [debouncedSetLockboxSearch],
  );

  const handleLockboxSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const val = e.target.value;
    setLockboxSearchInput(val);
    debouncedSetLockboxSearch(val);
  };

  const handleLockboxSearchClear = () => {
    setLockboxSearchInput('');
    setLockboxSearch('');
    debouncedSetLockboxSearch.cancel();
  };

  const userRole = loggedInUser ? resolveUserRole(loggedInUser) : undefined;
  const userId = loggedInUser?.id ?? '';
  const userOrgId = loggedInUser?.org?.id ?? '';
  const userLocations: string[] | undefined =
    userRole === 'branchAdmin'
      ? (loggedInUser as any as IBranchAdminUser).locations ||
        [(loggedInUser as any as IBranchAdminUser).location].filter(Boolean)
      : undefined;

  /**
   * Determines if the agent is limited to searching only within their office
   * for shackle permission grants (when assignee + office owner from different brokerage).
   */
  const getShackleSearchScopeRestriction = (): {
    restricted: boolean;
    reason: string;
  } | null => {
    if (permissionMode !== 'shackle' || !userRole) return null;
    if (selectedIds.size === 0) return null;

    for (const id of selectedIds) {
      const lb = allFetchedRowsById[id] ?? activeSource.find(l => l.id === id);
      if (lb) {
        const scope = getShackleScopeForLockbox(
          lb,
          userRole,
          userId,
          userOrgId,
        );
        if (scope === 'office') {
          return {
            restricted: true,
            reason:
              'One or more selected lockboxes has an office owner from a different brokerage. You can only grant shackle permissions to agents within your own office.',
          };
        }
        if (scope === 'none') {
          return {
            restricted: true,
            reason:
              'You do not have permission to grant shackle access for one or more selected lockboxes.',
          };
        }
      }
    }
    return null;
  };

  const shackleSearchRestriction = getShackleSearchScopeRestriction();

  const isUnassignMode = isAssigneeMode && assignMode === 'unassign';
  const isAssigneeUnassignMode = isAssigneeMode && assignMode === 'unassign';

  const isRowDisabled = (lb: LockboxRow): boolean => {
    if (!userRole) return true;

    // Assignee unassign mode: disable rows with no assignee
    if (isAssigneeUnassignMode) {
      if (!lb.assigneeId) return true;
      return !canUnassignAssigneeForLockbox(lb, userRole, userId, userOrgId);
    }

    // Owner change mode: disable if user cannot change owner for this lockbox
    if (permissionMode === 'owner' && assignMode === 'assign') {
      return !canChangeOwnerForLockbox(
        lb,
        userRole,
        userId,
        userOrgId,
        userLocations,
      );
    }

    // Assignee change mode: disable if user cannot change assignee for this lockbox
    if (permissionMode === 'assignee' && assignMode === 'assign') {
      if (
        !canChangeAssigneeForLockbox(
          lb,
          userRole,
          userId,
          userOrgId,
          userLocations,
        )
      )
        return true;
      // When assigning an office as assignee, only MLS-owned lockboxes are allowed
      if (
        ownerOrAssigneeType === 'office' &&
        (lb.ownerType === 'user' || lb.ownerType === 'office')
      ) {
        return true;
      }
      return false;
    }

    return false;
  };

  const getRowDisabledReason = (lb: LockboxRow): string | undefined => {
    if (!userRole) return 'User role could not be determined';

    if (isAssigneeUnassignMode) {
      if (!lb.assigneeId) return 'This lockbox has no assignee to unassign';
      if (!canUnassignAssigneeForLockbox(lb, userRole, userId, userOrgId)) {
        return 'You do not have permission to unassign this lockbox. Only the MLS staff admin or the owning brokerage admin can unassign.';
      }
    }

    if (permissionMode === 'owner' && assignMode === 'assign') {
      if (
        !canChangeOwnerForLockbox(
          lb,
          userRole,
          userId,
          userOrgId,
          userLocations,
        )
      ) {
        return 'You cannot change the owner of this lockbox. The owner change can only be completed by the MLS staff admin or the admin of the owning brokerage.';
      }
    }

    if (permissionMode === 'assignee' && assignMode === 'assign') {
      if (
        !canChangeAssigneeForLockbox(
          lb,
          userRole,
          userId,
          userOrgId,
          userLocations,
        )
      ) {
        return 'You cannot change the assignee of this lockbox. The assignee change can only be completed by the MLS staff admin or the admin of the owning brokerage.';
      }
      if (
        ownerOrAssigneeType === 'office' &&
        (lb.ownerType === 'user' || lb.ownerType === 'office')
      ) {
        return 'An office can only be assigned to lockboxes owned by the MLS/Board.';
      }
    }

    return undefined;
  };

  const toggleRow = (id: string) => {
    const lb = displayedLockboxes.find(l => l.id === id);
    if (lb && isRowDisabled(lb)) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleHeaderCheckbox = () => {
    if (allDisplayedSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        displayedLockboxes.forEach(lb => next.delete(lb.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        displayedLockboxes
          .filter(lb => !isRowDisabled(lb))
          .forEach(lb => next.add(lb.id));
        return next;
      });
    }
  };

  const lockboxLoadMoreRemaining = Math.max(
    0,
    fetchedTotal - fetchedLockboxes.length,
  );
  const handleLoadMoreLockboxes = () => setCurrentPage(prev => prev + 1);

  const handleGoToReview = () => {
    if (selectionState.validIds.length === 0) return;

    setSkippedIds(new Set(selectionState.skippedIds));
    setSkipMessage(selectionState.skipMessage);
    setReviewIds(selectionState.validIds);
    setReviewVisibleCount(REVIEW_PAGE_SIZE);
    setStep('review');
  };

  const hasValidSelectionForReview = selectionState.validIds.length > 0;

  const handleRemoveFromReview = (id: string) => {
    setReviewIds(prev => prev.filter(rid => rid !== id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleBackToLockboxSelection = () => setStep('selectLockboxes');

  const handleConfirmAndProcess = async () => {
    const targetType = (targetSelection?.type || '') as 'user' | 'office' | '';
    const targetId = targetSelection?.id || '';
    const targetLabel = targetSelection?.label || '';

    setProcessing(true);
    try {
      // Build per-lockbox assignee map for unassign mode
      const lockboxAssigneeMap: Record<string, string> = {};
      if (isAssigneeUnassignMode) {
        reviewIds.forEach(id => {
          const lb =
            allFetchedRowsById[id] ?? activeSource.find(l => l.id === id);
          if (lb?.assigneeId && lb.serialNumber) {
            lockboxAssigneeMap[lb.serialNumber] = lb.assigneeId;
          }
        });
      }
      const result = await onConfirmAndProcess?.({
        mode: permissionMode,
        assignMode,
        searchValue: targetId,
        ownerOrAssigneeType: targetType || ownerOrAssigneeType,
        target: targetSelection,
        targetId,
        targetType,
        targetLabel,
        assigneeId: permissionMode === 'assignee' ? targetId : undefined,
        assigneeType: permissionMode === 'assignee' ? targetType : undefined,
        selectedIds: reviewIds,
        selectedSerialNumbers: reviewIds
          .map(
            id =>
              (allFetchedRowsById[id] ?? activeSource.find(lb => lb.id === id))
                ?.serialNumber ?? '',
          )
          .filter(Boolean),
        lockboxAssigneeMap,
        grantOfficeShacklePermissions:
          (permissionMode === 'owner' || permissionMode === 'assignee') &&
          grantOfficeShacklePermissions,
      });

      const confirmResult = result || {
        successful: reviewIds.length,
        unsuccessful: 0,
      };

      // Build successfulLockboxes and failedLockboxes from review lockboxes
      // using failedIds/successfulIds when explicit lockbox arrays are not provided
      const failedIdSet = new Set(
        confirmResult.failedLockboxes?.map(lb => lb.id) ??
          confirmResult.failedIds ??
          [],
      );
      const successfulIdSet = new Set(
        confirmResult.successfulLockboxes?.map(lb => lb.id) ??
          confirmResult.successfulIds ??
          [],
      );

      if (!confirmResult.failedLockboxes && failedIdSet.size > 0) {
        const errorMap = new Map(
          (confirmResult.failedIdsWithErrors || []).map(e => [
            e.id,
            e.errorMessage,
          ]),
        );
        confirmResult.failedLockboxes = reviewLockboxes
          .filter(
            lb => failedIdSet.has(lb.id) || failedIdSet.has(lb.serialNumber),
          )
          .map(lb => ({
            ...lb,
            errorMessage:
              errorMap.get(lb.id) ||
              errorMap.get(lb.serialNumber) ||
              'An unexpected error occurred',
          }));
      }
      if (!confirmResult.successfulLockboxes) {
        if (successfulIdSet.size > 0) {
          confirmResult.successfulLockboxes = reviewLockboxes.filter(
            lb =>
              successfulIdSet.has(lb.id) ||
              successfulIdSet.has(lb.serialNumber),
          );
        } else {
          confirmResult.successfulLockboxes = reviewLockboxes.filter(
            lb => !failedIdSet.has(lb.id) && !failedIdSet.has(lb.serialNumber),
          );
        }
      }

      // Confirmation UI is based on pre-submit review rows. For shackle assignment,
      // reflect the updated permission count immediately on successful rows.
      if (
        permissionMode === 'shackle' &&
        assignMode === 'assign' &&
        targetType === 'user' &&
        targetId
      ) {
        const successfulLockboxes = confirmResult.successfulLockboxes || [];
        const updatedSuccessfulLockboxes = successfulLockboxes.map(lb => {
          if (lb.granteeUserIds.includes(targetId)) return lb;

          return {
            ...lb,
            granteeUserIds: [...lb.granteeUserIds, targetId],
            shacklePermissions: (lb.shacklePermissions ?? 0) + 1,
          };
        });

        confirmResult.successfulLockboxes = updatedSuccessfulLockboxes;

        setAllFetchedRowsById(prev => {
          const next = { ...prev };
          updatedSuccessfulLockboxes.forEach(lb => {
            next[lb.id] = lb;
          });
          return next;
        });
        setFetchedLockboxes(prev =>
          prev.map(lb => {
            const updated = updatedSuccessfulLockboxes.find(
              row => row.id === lb.id,
            );
            return updated || lb;
          }),
        );
      }

      setConfirmationResult(confirmResult);
      setConfirmationTab(
        confirmResult.unsuccessful > 0 ? 'unsuccessful' : 'successful',
      );
      setStep('confirmation');
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setCloseConfirmVisible(true);
      return;
    }
    resetState();
    onCancel?.();
  };

  const handleForceClose = () => {
    setCloseConfirmVisible(false);
    resetState();
    onCancel?.();
  };

  // "Assign more lockboxes" — reset form/target state and force a lockbox
  // refetch so the table is up-to-date after the previous assignment.
  const handleStartNew = () => {
    resetUserSelection();
    setLockboxFetchTrigger(prev => prev + 1);
  };

  // Resets only form / target / selection state. Fetched lockboxes are kept
  // in memory so they don't need to be re-fetched on every mode switch.
  const resetUserSelection = () => {
    setStep('selectAction');
    setPermissionMode('owner');
    setAssignMode('assign');
    setOwnerOrAssigneeType('user');
    setGrantOfficeShacklePermissions(false);
    setSearchTarget('');
    setSearchTargetLabel('');
    setTargetSelection(null);
    setSelectedTargetResult(null);
    setSearchResults([]);
    setSearchLoading(false);
    setLockboxSearchInput('');
    setLockboxSearch('');
    setStatusFilter('');
    setSelectedIds(new Set());
    setCurrentPage(1);
    setReviewIds([]);
    setReviewVisibleCount(REVIEW_PAGE_SIZE);
    setSkippedIds(new Set());
    setSkipMessage('');
    setConfirmationResult(null);
    setConfirmationTab('successful');
  };

  // Full reset including cached lockbox data — called only on modal close.
  const resetState = () => {
    resetUserSelection();
    setFetchedLockboxes([]);
    setFetchedTotal(0);
    setAllFetchedRowsById({});
    setLockboxesLoading(false);
    setLockboxFetchTrigger(0);
  };

  const renderStepper = () => {
    const isConfirmation = step === 'confirmation';
    const currentIndex = isConfirmation
      ? WIZARD_STEPS.length
      : WIZARD_STEPS.findIndex(s => s.key === step);
    if (!isConfirmation && currentIndex === -1) return null;

    return (
      <StepperContainer>
        {WIZARD_STEPS.map((wizardStep, index) => (
          <React.Fragment key={wizardStep.key}>
            <StepperItem>
              <StepperCircle
                $active={!isConfirmation && index === currentIndex}
                $completed={isConfirmation || index < currentIndex}
              >
                {isConfirmation || index < currentIndex ? (
                  <CheckOutlined />
                ) : (
                  index + 1
                )}
              </StepperCircle>
              <StepperLabel $active={!isConfirmation && index === currentIndex}>
                {wizardStep.key === 'chooseType' && permissionMode === 'owner'
                  ? 'Choose type'
                  : wizardStep.label}
              </StepperLabel>
            </StepperItem>
            {index < WIZARD_STEPS.length - 1 && (
              <StepperLine
                $completed={isConfirmation || index < currentIndex}
              />
            )}
          </React.Fragment>
        ))}
      </StepperContainer>
    );
  };

  const handleNextFromSelectAction = () => setStep('chooseType');

  const handleBackFromChooseType = () => {
    setStep('selectAction');
    setAssignMode('assign');
    setSearchTargetLabel('');
    setSearchTarget('');
    setTargetSelection(null);
    setSelectedTargetResult(null);
    setSearchResults([]);
  }

  const handleNextFromChooseType = () => setStep('selectLockboxes');

  const handleBackFromLockboxes = () => {
    setStep('chooseType');
    setSelectedIds(new Set());
    setCurrentPage(1);
    setLockboxSearchInput('');
    setLockboxSearch('');
    setStatusFilter('');
    setReviewIds([]);
    setSkippedIds(new Set());
  };

  const renderSelectActionStep = () => (
    <>
      <StyledSection>
        <SectionLabel>Select an action</SectionLabel>
        {PERMISSION_MODE_OPTIONS.map(option => (
          <ActionCard
            key={option.value}
            $selected={permissionMode === option.value}
            onClick={() => {
              setPermissionMode(option.value);
              setAssignMode('assign');
              setOwnerOrAssigneeType('user');
              setGrantOfficeShacklePermissions(false);
              setSearchTarget('');
              setSearchTargetLabel('');
              setTargetSelection(null);
              setSelectedTargetResult(null);
              setSearchResults([]);
              setSelectedIds(new Set());
              setLockboxSearch('');
              setStatusFilter('');
              setCurrentPage(1);
            }}
          >
            <ActionCardIcon $mode={option.value}>{option.icon}</ActionCardIcon>
            <ActionCardContent>
              <ActionCardTitle>{option.label}</ActionCardTitle>
              <ActionCardDescription>
                {option.description}
              </ActionCardDescription>
            </ActionCardContent>
          </ActionCard>
        ))}
      </StyledSection>
    </>
  );

  const renderChooseTypeStep = () => {
    const selectedTargetDisplay =
      selectedTargetResult ||
      (targetSelection
        ? {
            id: targetSelection.id,
            type: targetSelection.type,
            name: targetSelection.label,
            officeName:
              targetSelection.type === 'office'
                ? targetSelection.label
                : undefined,
            firstName:
              targetSelection.type === 'user'
                ? targetSelection.label
                : undefined,
          }
        : null);

    return (
      <>
        <StyledSection>
          <SectionLabel>
            {permissionMode === 'owner'
              ? 'Assign lockboxes to a new owner'
              : permissionMode === 'assignee'
                ? 'Change assignee'
                : 'Give shackle permissions to an agent'}
          </SectionLabel>

          {isAssigneeMode && (
            <AssignModeCardsRow>
              <AssignModeCard
                $selected={assignMode === 'assign'}
                onClick={() => setAssignMode('assign')}
                type="button"
              >
                <AssignModeCardIcon $selected={assignMode === 'assign'}>
                  <UserAddOutlined />
                </AssignModeCardIcon>
                <AssignModeCardText>
                  Assign an assignee to the selected lockboxes
                </AssignModeCardText>
              </AssignModeCard>
              <AssignModeCard
                $selected={assignMode === 'unassign'}
                onClick={() => setAssignMode('unassign')}
                type="button"
              >
                <AssignModeCardIcon $selected={assignMode === 'unassign'}>
                  <MinusCircleOutlined />
                </AssignModeCardIcon>
                <AssignModeCardText>
                  Unassign all assignees from the selected lockboxes
                </AssignModeCardText>
              </AssignModeCard>
            </AssignModeCardsRow>
          )}

          {!isUnassignMode &&
            (targetSelection ? (
              <SelectedTargetCard>
                <SelectedTargetContent>
                  <SelectedTargetSummary>
                    <SelectedTargetAvatar>
                      {getSelectedTargetInitials(selectedTargetDisplay as any)}
                    </SelectedTargetAvatar>
                    <SelectedTargetText>
                      <SelectedTargetTitle>
                        {getSelectedTargetTitle(selectedTargetDisplay as any)}
                      </SelectedTargetTitle>
                      {getSelectedTargetSubtitle(
                        selectedTargetDisplay as any,
                      ) ? (
                        <SelectedTargetSubtitle>
                          {getSelectedTargetSubtitle(
                            selectedTargetDisplay as any,
                          )}
                        </SelectedTargetSubtitle>
                      ) : null}
                    </SelectedTargetText>
                  </SelectedTargetSummary>
                </SelectedTargetContent>
                <SelectedTargetRemoveButton
                  type="button"
                  onClick={() => {
                    setSearchTarget('');
                    setSearchTargetLabel('');
                    setTargetSelection(null);
                    setSelectedTargetResult(null);
                  }}
                  aria-label="Clear selected target"
                >
                  <CloseOutlined />
                </SelectedTargetRemoveButton>
              </SelectedTargetCard>
            ) : (
              <StyledSearchRow>
                <Select
                  value={ownerOrAssigneeType}
                  className={'agent-type-select'}
                  onChange={(val: string) => {
                    setOwnerOrAssigneeType(val);
                    setGrantOfficeShacklePermissions(false);
                    setSearchTarget('');
                    setSearchTargetLabel('');
                    setTargetSelection(null);
                    setSelectedTargetResult(null);
                    setSearchResults([]);
                  }}
                  style={{ width: 110 }}
                >
                  {(permissionMode === 'shackle'
                    ? AGENT_TYPE_OPTIONS.filter(opt => opt.value === 'user')
                    : AGENT_TYPE_OPTIONS
                  ).map(opt => (
                    <SelectOption key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectOption>
                  ))}
                </Select>
                <Select
                  showSearch
                  filterOption={false}
                  showArrow={false}
                  allowClear
                  optionLabelProp="title"
                  value={searchTarget || undefined}
                  placeholder={
                    ownerOrAssigneeType === 'user'
                      ? 'Search for user'
                      : ownerOrAssigneeType === 'office'
                        ? 'Search for office'
                        : 'Search'
                  }
                  placement="bottomLeft"
                  listHeight={300}
                  dropdownStyle={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    borderRadius: 5,
                  }}
                  getPopupContainer={() => document.body}
                  zIndex={1100}
                  onSearch={debouncedTargetSearch}
                  onChange={(val?: string) => {
                    const selectedValue = val || '';
                    setSearchTarget(selectedValue);
                    if (!selectedValue) {
                      setSearchTargetLabel('');
                      setTargetSelection(null);
                      setSelectedTargetResult(null);
                      return;
                    }

                    const selected = searchResults.find(
                      item => `${item.id}|${item.type}` === selectedValue,
                    );
                    if (
                      selected?.id &&
                      (selected.type === 'user' || selected.type === 'office')
                    ) {
                      const label = getSearchResultDisplayName(selected);
                      setSearchTargetLabel(label);
                      setTargetSelection({
                        id: selected.id,
                        type: selected.type,
                        label,
                        orgId:
                          selected.type === 'office'
                            ? selected.id
                            : selected.orgId,
                      });
                      setSelectedTargetResult(selected);
                      return;
                    }

                    const [rawId, rawType] = selectedValue.split('|');
                    if (rawId && (rawType === 'user' || rawType === 'office')) {
                      setSearchTargetLabel(rawId);
                      setTargetSelection({
                        id: rawId,
                        type: rawType,
                        label: rawId,
                        orgId: rawType === 'office' ? rawId : undefined,
                      });
                      setSelectedTargetResult({
                        id: rawId,
                        type: rawType,
                        name: rawId,
                        officeName: rawType === 'office' ? rawId : undefined,
                        firstName: rawType === 'user' ? rawId : undefined,
                      });
                    } else {
                      setSearchTargetLabel('');
                      setTargetSelection(null);
                      setSelectedTargetResult(null);
                    }
                  }}
                  notFoundContent={searchLoading ? <Loading /> : null}
                  style={{ flex: 1 }}
                >
                  {searchResults.map(item => (
                    <SelectOption
                      key={`${item.id}|${item.type}`}
                      value={`${item.id}|${item.type}`}
                      title={getSearchResultDisplayName(item)}
                    >
                      {SearchLabel(item as any, true)}
                    </SelectOption>
                  ))}
                </Select>
              </StyledSearchRow>
            ))}

          {(permissionMode === 'owner' || permissionMode === 'assignee') &&
            !isUnassignMode && (
              <>
                <ShackleCheckboxWrapper>
                  <span style={{ opacity: 1, cursor: 'pointer' }}>
                    <AntdCheckbox
                      checked={grantOfficeShacklePermissions}
                      onChange={e =>
                        setGrantOfficeShacklePermissions(e.target.checked)
                      }
                    >
                      Grant shackle permissions to everyone in the office
                    </AntdCheckbox>
                  </span>
                </ShackleCheckboxWrapper>

                {grantOfficeShacklePermissions && (
                  <InfoBox>
                    <InfoBoxHeader>
                      <ExclamationCircleFilled
                        style={{
                          color: '#faad14',
                          fontSize: '32px',
                          alignItems: 'flex-start',
                        }}
                      />
                      <span>
                        <p style={{ fontSize: '14px' }}>
                          All subscribed users in the new owner&apos;s office
                          will be granted shackle permissions for the selected
                          lockboxes.
                        </p>
                        <p style={{ fontSize: '10px' }}>
                          Users in the office who subscribe to their key in the
                          future will automatically get shackle permissions.
                        </p>
                      </span>
                    </InfoBoxHeader>
                  </InfoBox>
                )}
              </>
            )}

          {permissionMode === 'shackle' &&
            shackleSearchRestriction?.restricted && (
              <InfoBox>
                <InfoBoxHeader>
                  <ExclamationCircleFilled
                    style={{ color: '#faad14', fontSize: '24px' }}
                  />
                  <span>{shackleSearchRestriction.reason}</span>
                </InfoBoxHeader>
              </InfoBox>
            )}
        </StyledSection>
      </>
    );
  };

  const renderSelectLockboxesStep = () => (
    <>
      {selectionState.warningMessage && (
        <SkipMessageBox $type="warning" style={{ marginBottom: '4px' }}>
          {selectionState.warningMessage}
        </SkipMessageBox>
      )}

      <StyledDivider />

      <StyledSection>
        <SectionLabel>Select Lockboxes</SectionLabel>

        <StyledToolbar>
          <Input
            prefix={<SearchOutlined />}
            suffix={
              lockboxSearchInput ? (
                <CloseOutlined
                  style={{ cursor: 'pointer', color: '#aaa' }}
                  onClick={handleLockboxSearchClear}
                />
              ) : null
            }
            placeholder="Search by serial number or agent"
            value={lockboxSearchInput}
            onChange={handleLockboxSearchChange}
            style={{ width: '100%' }}
          />
          <Select
            value={statusFilter}
            onChange={(val: string) => setStatusFilter(val)}
            style={{ width: 150 }}
          >
            {STATUS_OPTIONS.map(opt => (
              <SelectOption key={opt.value} value={opt.value}>
                {opt.label}
              </SelectOption>
            ))}
          </Select>
        </StyledToolbar>

        <StyledTable>
          <StyledThead>
            <tr>
              <StyledTh style={{ width: 40 }}>
                <AntdCheckbox
                  checked={allDisplayedSelected}
                  indeterminate={!allDisplayedSelected && someSelected}
                  onChange={handleHeaderCheckbox}
                />
              </StyledTh>
              <StyledTh>Box Type</StyledTh>
              <StyledTh>Owner</StyledTh>
              <StyledTh>Assignee</StyledTh>
              <StyledTh>Shackle Permissions</StyledTh>
              <StyledTh>Status</StyledTh>
            </tr>
          </StyledThead>
          <tbody>
            {lockboxesLoading && currentPage === 1 ? (
              <tr>
                <StyledTd
                  colSpan={6}
                  style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: 'rgba(0,0,0,0.45)',
                  }}
                >
                  <Loading size="fa-3x" />
                </StyledTd>
              </tr>
            ) : displayedLockboxes.length === 0 ? (
              <tr>
                <StyledTd
                  colSpan={6}
                  style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: 'rgba(0,0,0,0.45)',
                  }}
                >
                  No lockboxes found
                </StyledTd>
              </tr>
            ) : (
              displayedLockboxes.map(lb => {
                const disabled = isRowDisabled(lb);
                return (
                  <StyledTr
                    key={lb.id}
                    $selected={selectedIds.has(lb.id)}
                    onClick={() => !disabled && toggleRow(lb.id)}
                    style={
                      disabled
                        ? { opacity: 0.5, cursor: 'not-allowed' }
                        : undefined
                    }
                    title={disabled ? getRowDisabledReason(lb) : undefined}
                  >
                    <StyledTd onClick={e => e.stopPropagation()}>
                      <AntdCheckbox
                        checked={selectedIds.has(lb.id)}
                        onChange={() => toggleRow(lb.id)}
                        disabled={disabled}
                      />
                    </StyledTd>
                    <StyledTd>{lb.boxType}</StyledTd>
                    <StyledTd>
                      {lb.owner ?? <Tag color="default">Unassigned</Tag>}
                    </StyledTd>
                    <StyledTd>
                      {lb.assignee ?? <Tag color="default">Unassigned</Tag>}
                    </StyledTd>
                    <StyledTd>
                      {lb.shacklePermissions !== null ? (
                        <Tag color="green">{lb.shacklePermissions} users</Tag>
                      ) : (
                        <span style={{ color: '#8c8c8c' }}>None</span>
                      )}
                    </StyledTd>
                    <StyledTd>
                      <LockboxStatusTag lockboxStatus={lb.status as any} />
                    </StyledTd>
                  </StyledTr>
                );
              })
            )}
          </tbody>
        </StyledTable>

        {lockboxesLoading && currentPage > 1 && fetchedLockboxes.length > 0 ? (
          <LoadMoreRow>
            <Loading size="fa-lg" />
          </LoadMoreRow>
        ) : lockboxLoadMoreRemaining > 0 && !lockboxesLoading ? (
          <LoadMoreRow onClick={handleLoadMoreLockboxes}>
            <LoadMoreButton type="button">
              <span aria-hidden="true">↓</span>
              Load more
            </LoadMoreButton>
            <LoadMoreRemaining>
              {lockboxLoadMoreRemaining} remaining
            </LoadMoreRemaining>
          </LoadMoreRow>
        ) : null}
      </StyledSection>
    </>
  );

  const reviewActionLabel = (() => {
    const actionVerb = assignMode === 'unassign' ? 'Unassign' : 'Assign';
    const roleLabel =
      permissionMode === 'assignee'
        ? 'an assignee'
        : permissionMode === 'owner'
          ? 'an owner'
          : 'shackle permissions';
    const direction = assignMode === 'unassign' ? 'from' : 'to';
    return `${actionVerb} ${roleLabel} ${direction} the selected lockboxes`;
  })();

  const renderReviewStep = () => {
    const visibleReviewLockboxes = reviewLockboxes.slice(0, reviewVisibleCount);
    const reviewLoadMoreRemaining =
      reviewLockboxes.length - visibleReviewLockboxes.length;

    return (
      <>
        {/* Skip message (if any lockboxes were skipped) */}
        {skipMessage && (
          <SkipMessageBox
            $type={skippedIds.size === selectedIds.size ? 'warning' : 'info'}
          >
            {skipMessage}
          </SkipMessageBox>
        )}

        {/* Change Summary cards */}
        <SummaryCardsRow>
          <ReviewActionCard>
            <SummaryCardLabel>Action</SummaryCardLabel>
            <SummaryCardValue>{reviewActionLabel}</SummaryCardValue>
          </ReviewActionCard>
          <ReviewLockboxCountCard>
            <SummaryCardLabel>Lockboxes</SummaryCardLabel>
            <SummaryCardValue>{reviewIds.length}</SummaryCardValue>
          </ReviewLockboxCountCard>
        </SummaryCardsRow>

        {/* Lockbox review table */}
        <StyledTable>
          <StyledThead>
            <tr>
              <StyledTh>Box Type</StyledTh>
              <StyledTh>{currentFieldLabel}</StyledTh>
              <StyledTh>{newFieldLabel}</StyledTh>
              <StyledTh style={{ width: 40 }} />
            </tr>
          </StyledThead>
          <tbody>
            {visibleReviewLockboxes.map(lb => {
              const currentValue = isAssigneeMode
                ? lb.assignee
                : permissionMode === 'owner'
                  ? lb.owner
                  : lb.shacklePermissions !== null
                    ? `${lb.shacklePermissions} users`
                    : null;

              return (
                <ReviewTr key={lb.id}>
                  <StyledTd>{lb.boxType}</StyledTd>
                  <StyledTd>
                    {currentValue ?? <Tag color="default">Unassigned</Tag>}
                  </StyledTd>
                  <StyledTd>
                    {isAssigneeMode && assignMode === 'unassign' ? (
                      <span style={{ color: '#f5222d' }}>Unassigned</span>
                    ) : (
                      <span style={{ color: '#52c41a' }}>
                        {searchTargetLabel || '—'}
                      </span>
                    )}
                  </StyledTd>
                  <StyledTd>
                    <RemoveButton onClick={() => handleRemoveFromReview(lb.id)}>
                      <CloseOutlined
                        style={{
                          fontSize: '12px',
                          padding: '3px',
                          color: '#000000',
                        }}
                      />
                    </RemoveButton>
                  </StyledTd>
                </ReviewTr>
              );
            })}
          </tbody>
        </StyledTable>

        {reviewLoadMoreRemaining > 0 && (
          <LoadMoreRow
            onClick={() =>
              setReviewVisibleCount(prev => prev + REVIEW_PAGE_SIZE)
            }
          >
            <LoadMoreButton type="button">
              <span aria-hidden="true">↓</span>
              Load more
            </LoadMoreButton>
            <LoadMoreRemaining>
              {reviewLoadMoreRemaining} remaining
            </LoadMoreRemaining>
          </LoadMoreRow>
        )}
      </>
    );
  };

  const renderConfirmationStep = () => {
    if (!confirmationResult) return null;
    const totalProcessed =
      confirmationResult.successful + confirmationResult.unsuccessful;

    const successfulLockboxes = confirmationResult.successfulLockboxes || [];
    const failedLockboxes = confirmationResult.failedLockboxes || [];

    const getPreviousValue = (lb: LockboxRow) => {
      if (isAssigneeMode) return lb.assignee;
      if (permissionMode === 'owner') return lb.owner;
      return lb.shacklePermissions !== null
        ? `${lb.shacklePermissions} users`
        : null;
    };

    return (
      <>
        {/* Header */}
        <ConfirmationHeader>
          {allSuccess ? (
            <CheckCircleFilled style={{ fontSize: 48, color: '#52c41a' }} />
          ) : (
            <ExclamationCircleFilled
              style={{ fontSize: 48, color: '#1890ff' }}
            />
          )}
          <ConfirmationHeaderText>
            <ConfirmationTitle>Changes applied</ConfirmationTitle>
            <ConfirmationSubtitle>
              {allSuccess
                ? 'All lockboxes were updated successfully'
                : `${confirmationResult.successful}/${totalProcessed} lockboxes were updated successfully`}
            </ConfirmationSubtitle>
          </ConfirmationHeaderText>
        </ConfirmationHeader>

        {/* Tabbed results section */}
        <ConfirmationResultsCard>
          <ConfirmationTabsRow>
            <ConfirmationTab
              $active={confirmationTab === 'successful'}
              onClick={() => setConfirmationTab('successful')}
            >
              Successful ({confirmationResult.successful})
            </ConfirmationTab>
            <ConfirmationTab
              $active={confirmationTab === 'unsuccessful'}
              onClick={() => setConfirmationTab('unsuccessful')}
            >
              Unsuccessful ({confirmationResult.unsuccessful})
            </ConfirmationTab>
          </ConfirmationTabsRow>

          {confirmationTab === 'successful' && (
            <StyledTable>
              <StyledThead>
                <tr>
                  <StyledTh>Box Type</StyledTh>
                  <StyledTh>{currentFieldLabel}</StyledTh>
                  <StyledTh>{newFieldLabel}</StyledTh>
                </tr>
              </StyledThead>
              <tbody>
                {successfulLockboxes.length === 0 ? (
                  <ReviewTr>
                    <StyledTd
                      colSpan={3}
                      style={{
                        textAlign: 'center',
                        padding: '24px',
                        color: 'rgba(0,0,0,0.45)',
                      }}
                    >
                      No successful lockboxes
                    </StyledTd>
                  </ReviewTr>
                ) : (
                  successfulLockboxes.map(lb => (
                    <ReviewTr key={lb.id}>
                      <StyledTd>{lb.boxType}</StyledTd>
                      <StyledTd>
                        {getPreviousValue(lb) ?? (
                          <Tag color="default">Unassigned</Tag>
                        )}
                      </StyledTd>
                      <StyledTd>
                        {isAssigneeMode && assignMode === 'unassign' ? (
                          <Tag color="default">Unassigned</Tag>
                        ) : (
                          <span style={{ color: '#52c41a' }}>
                            {searchTargetLabel || '—'}
                          </span>
                        )}
                      </StyledTd>
                    </ReviewTr>
                  ))
                )}
              </tbody>
            </StyledTable>
          )}

          {confirmationTab === 'unsuccessful' && (
            <StyledTable>
              <StyledThead>
                <tr>
                  <StyledTh>Box Type</StyledTh>
                  <StyledTh>{currentFieldLabel}</StyledTh>
                  <StyledTh>{newFieldLabel}</StyledTh>
                </tr>
              </StyledThead>
              <tbody>
                {failedLockboxes.length === 0 ? (
                  <ReviewTr>
                    <StyledTd
                      colSpan={3}
                      style={{
                        textAlign: 'center',
                        padding: '24px',
                        color: 'rgba(0,0,0,0.45)',
                      }}
                    >
                      No unsuccessful lockboxes
                    </StyledTd>
                  </ReviewTr>
                ) : (
                  failedLockboxes.map(lb => (
                    <ReviewTr key={lb.id}>
                      <StyledTd>{lb.boxType}</StyledTd>
                      <StyledTd>
                        {getPreviousValue(lb) ?? (
                          <Tag color="default">Unassigned</Tag>
                        )}
                      </StyledTd>
                      <StyledTd>
                        <ConfirmationErrorCell>
                          <Tag color="red">Error</Tag>
                          {lb.errorMessage && (
                            <span
                              style={{
                                fontSize: 13,
                                color: 'rgba(0,0,0,0.65)',
                              }}
                            >
                              {lb.errorMessage}
                            </span>
                          )}
                        </ConfirmationErrorCell>
                      </StyledTd>
                    </ReviewTr>
                  ))
                )}
              </tbody>
            </StyledTable>
          )}
        </ConfirmationResultsCard>
      </>
    );
  };

  const renderFooter = () => {
    switch (step) {
      case 'selectAction':
        return (
          <StyledFooter>
            <Button onClick={handleClose}>Back</Button>
            <Button type="primary" onClick={handleNextFromSelectAction}>
              Next: choose action
            </Button>
          </StyledFooter>
        );
      case 'chooseType':
        return (
          <StyledFooter>
            <Button onClick={handleBackFromChooseType}>Back</Button>
            <Button
              type="primary"
              onClick={handleNextFromChooseType}
              disabled={!targetSelection && !isUnassignMode}
            >
              Next: select lockboxes
            </Button>
          </StyledFooter>
        );
      case 'selectLockboxes':
        return (
          <SelectLockboxesFooter>
            <Button onClick={handleBackFromLockboxes}>Back</Button>
            <SelectLockboxesFooterRight>
              <FooterTotal>Total: {displayedLockboxes.filter(lb => selectedIds.has(lb.id)).length}</FooterTotal>
              <Button
                type="primary"
                onClick={handleGoToReview}
                disabled={!hasValidSelectionForReview}
              >
                Next: review and confirm
              </Button>
            </SelectLockboxesFooterRight>
          </SelectLockboxesFooter>
        );
      case 'review':
        return (
          <SelectLockboxesFooter>
            <Button
              onClick={handleBackToLockboxSelection}
              disabled={processing}
            >
              Back
            </Button>
            <SelectLockboxesFooterRight>
              <FooterTotal>Total: {reviewIds.length}</FooterTotal>
              <Button
                type="primary"
                onClick={handleConfirmAndProcess}
                disabled={reviewIds.length === 0 || processing}
                loading={processing}
              >
                Confirm
              </Button>
            </SelectLockboxesFooterRight>
          </SelectLockboxesFooter>
        );
      case 'confirmation':
        return (
          <StyledFooter>
            <Button onClick={handleClose}>Close</Button>
            <Button type="primary" onClick={handleStartNew}>
              {'Start new bulk action'}
            </Button>
          </StyledFooter>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Prompt
        when={hasUnsavedChanges}
        message="You have unsaved changes in the Manage Permissions screen. Are you sure you want to leave?"
      />
      <LockboxPermissionsManagerModalStyles />
      <Modal
        title="Lockbox Permissions Manager"
        open={visible}
        onCancel={handleClose}
        width={760}
        footer={renderFooter()}
        maskClosable={false}
        destroyOnClose
        wrapClassName="lockbox-permissions-manager-modal"
      >
        {renderStepper()}
        {step === 'selectAction' && renderSelectActionStep()}
        {step === 'chooseType' && renderChooseTypeStep()}
        {step === 'selectLockboxes' && renderSelectLockboxesStep()}
        {step === 'review' && renderReviewStep()}
        {step === 'confirmation' && renderConfirmationStep()}
      </Modal>

      <Modal
        title="Discard changes?"
        open={closeConfirmVisible}
        onCancel={() => setCloseConfirmVisible(false)}
        onOk={handleForceClose}
        okText="Yes, close"
        okButtonProps={{ danger: true }}
        cancelText="Stay"
        width={420}
        maskClosable={false}
        zIndex={1100}
      >
        You have unsaved changes. Are you sure you want to close the Manage
        Permissions screen? Your selections will be lost.
      </Modal>
    </>
  );
};

const LockboxPermissionsManagerModalStyles = createGlobalStyle`
  .lockbox-permissions-manager-modal .ant-modal {
    top: 2rem;
    padding: 2rem 0 5rem;
  }

  .lockbox-permissions-manager-modal .ant-modal-content {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 7rem);
    border-radius: 12px;
    overflow: hidden;
  }

  .lockbox-permissions-manager-modal .ant-modal-header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .lockbox-permissions-manager-modal .ant-modal-title {
    font-size: 16px;
    font-weight: 600;
  }

  .lockbox-permissions-manager-modal .ant-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .lockbox-permissions-manager-modal .ant-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
  }

  /* Dropdown renders inside modal body */
  .lockbox-permissions-manager-modal .ant-select-dropdown {
    z-index: 1051;
  }
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 20px;
`;

const SectionLabel = styled.p`
  margin: 0;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const StyledDivider = styled.hr`
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 20px 0;
`;

const StyledSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &:focus-within {
    border-color: #1677ff;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
  }

  /* LabelWrapper around the type-picker: don't grow */
  & > div:first-child {
    flex-shrink: 0;
  }

  /* LabelWrapper around the search Select: fill remaining space */
  & > div:last-child {
    flex: 1;
    min-width: 0;
    width: 100%;
  }

  /* Type picker (first Select) */
  .ant-select:first-child .ant-select-selector,
  .agent-type-select .ant-select-selector {
    border: none !important;
    border-right: 1px solid #f0f0f0 !important;
    border-radius: 0 !important;
    background-color: #fafafa !important;
    min-width: 90px;
    height: 40px !important;
    display: flex !important;
    align-items: center !important;
    .ant-select-selection-item {
      align-items: center;
    }
  }

  /* Search Select (second Select) */
  .ant-select:not(:first-child) {
    width: 100% !important;
  }

  .ant-select:not(:first-child) .ant-select-selector {
    border: none !important;
    border-radius: 0 !important;
    background-color: #fff !important;
    height: 40px !important;
    padding: 4px 12px !important;
  }

  .ant-select-selection-search-input {
    height: 32px !important;
  }

  .ant-input {
    border: none;
    box-shadow: none;
  }
`;

const SelectedTargetCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  padding: 10px 14px;
`;

const SelectedTargetContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SelectedTargetSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const SelectedTargetAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e6f4ff;
  color: #1677ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
`;

const SelectedTargetText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const SelectedTargetTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.85);
`;

const SelectedTargetSubtitle = styled.div`
  font-size: 12px;
  padding-top: 5px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.75);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SelectedTargetRemoveButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #cdcdcd;
  border-radius: 10px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.65);
  cursor: pointer;

  &:hover {
    color: rgba(0, 0, 0, 0.88);
    border-color: #bfbfbf;
  }
`;

const ShackleCheckboxWrapper = styled.div`
  margin-top: -4px;
`;

const StyledToolbar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;

  &&& > div:first-child {
    flex: 1;
    min-width: 0;

    .ant-input-affix-wrapper {
      background: #fff !important;
      border-color: rgba(0, 0, 0, 0.15) !important;
      box-shadow: none !important;
    }

    .ant-input {
      background: #fff !important;
    }
  }

  &&& > div:last-child {
    flex-shrink: 0;

    .ant-select-selector {
      background: #fff !important;
      border-color: rgba(0, 0, 0, 0.15) !important;
      box-shadow: none !important;
    }
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
`;

const StyledThead = styled.thead`
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
`;

const StyledTh = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
`;

const StyledTr = styled.tr<{ $selected: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#e6f4ff' : 'transparent')};
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: ${({ $selected }) => ($selected ? '#bae0ff' : '#fafafa')};
  }
`;

const ReviewTr = styled.tr`
  border-bottom: 1px solid #f0f0f0;
`;

const StyledTd = styled.td`
  padding: 10px 12px;
  vertical-align: middle;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const SelectLockboxesFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const SelectLockboxesFooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const FooterTotal = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  font-weight: 500;
`;

const LoadMoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 6px;
  min-height: 36px;
  cursor: pointer;
`;

const LoadMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 600;
  font-size: 14px;
`;

const LoadMoreRemaining = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
`;

const AssignModeCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const AssignModeCard = styled.button<{ $selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid ${({ $selected }) => ($selected ? '#1677ff' : '#d9d9d9')};
  border-radius: 6px;
  background: ${({ $selected }) => ($selected ? '#e6f4ff' : '#fff')};
  color: rgba(0, 0, 0, 0.85);
  padding: 14px 14px;
  text-align: left;
  cursor: pointer;
`;

const AssignModeCardIcon = styled.div<{ $selected: boolean }>`
  width: 34px;
  height: 30px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $selected }) => ($selected ? '#dcecff' : '#eef5ff')};
  color: #1677ff;
  font-size: 17px;
  flex-shrink: 0;
`;

const AssignModeCardText = styled.span`
  font-size: 12px;
  line-height: 1.35;
  font-weight: 600;
`;

const InfoBox = styled.div`
  margin-top: 10px;
  border: 1px solid #ffe58f;
  border-radius: 10px;
  background: #fffbe6;
  color: rgba(0, 0, 0, 0.88);
  padding: 16px 18px;
`;

const InfoBoxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
`;

const InfoBoxBody = styled.p`
  margin: 10px 0 0 32px;
  font-size: 10px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.75);
`;

const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 0 20px;
`;

const StepperItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const StepperCircle = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $active, $completed }) =>
    $active ? '#1677ff' : $completed ? '#e6f4ff' : '#f0f0f0'};
  color: ${({ $active, $completed }) =>
    $active ? '#fff' : $completed ? '#1677ff' : 'rgba(0,0,0,0.45)'};
  flex-shrink: 0;
`;

const StepperLabel = styled.span<{ $active: boolean }>`
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  color: ${({ $active }) =>
    $active ? 'rgba(0,0,0,0.95)' : 'rgba(0,0,0,0.65)'};
  white-space: nowrap;
`;

const StepperLine = styled.div<{ $completed: boolean }>`
  flex: 1;
  height: 1px;
  background: ${({ $completed }) => ($completed ? '#1677ff' : '#f0f0f0')};
  margin: 0 8px;
`;

const ActionCard = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  border: 1.5px solid ${({ $selected }) => ($selected ? '#1677ff' : '#d9d9d9')};
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? '#e6f4ff' : '#fff')};
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;

  &:hover {
    border-color: #1677ff;
  }
`;

const ActionCardIcon = styled.div<{ $mode: PermissionMode }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  background: ${({ $mode }) =>
    $mode === 'owner'
      ? '#e6f4ff'
      : $mode === 'assignee'
        ? '#f6ffed'
        : '#fff7e6'};
  color: ${({ $mode }) =>
    $mode === 'owner'
      ? '#1677ff'
      : $mode === 'assignee'
        ? '#52c41a'
        : '#fa8c16'};
`;

const ActionCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ActionCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const ActionCardDescription = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #1e1e1e;
`;

const SkipMessageBox = styled.div<{ $type: 'warning' | 'info' }>`
  padding: 12px 16px;
  border-radius: 4px;
  margin: 12px 0;
  border-left: 4px solid
    ${({ $type }) => ($type === 'warning' ? '#faad14' : '#1890ff')};
  background: ${({ $type }) => ($type === 'warning' ? '#fffbe6' : '#e6f7ff')};
  color: rgba(0, 0, 0, 0.85);
  font-size: 14px;
  line-height: 1.5;
`;

const SummaryCardsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const SummaryCardLabel = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
`;

const SummaryCardValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`;

const ReviewActionCard = styled.div`
  flex: 1;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  background: #f5f5f5;
  width: 50%;
`;

const ReviewLockboxCountCard = styled.div`
  flex: 0 0 auto;
  min-width: 200px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  background: #f5f5f5;
  width: 50%;
`;

const RemoveButton = styled.button`
  background: none;
  border: 1px solid #cdcdcd;
  border-radius: 8px;
  box-shadow: 0 2px 0 0 rgba(0, 0, 0, 0.02);
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    color: rgba(0, 0, 0, 0.85);
  }
`;

const ConfirmationHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
`;

const ConfirmationHeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ConfirmationTitle = styled.h3`
  margin: 0 0 4px;
  font-weight: 600;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.85);
`;

const ConfirmationSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
`;

const ConfirmationResultsCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  flex-shrink: 0;
`;

const ConfirmationTabsRow = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 20px;
`;

const ConfirmationTab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? '#1677ff' : 'transparent')};
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#1677ff' : 'rgba(0, 0, 0, 0.65)')};
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s;

  &:hover {
    color: #1677ff;
  }
`;

const ConfirmationErrorCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
